import {
  MatrixData,
  AdditionalItemsData,
  RetainingWallData,
  AttenuationCrateData,
  CompoundAreaData,
  CustomCalculationData,
} from "../types/tender.types";
import {
  calculateRetainingWallCosts,
  calculateAttenuationCrateCosts,
  calculateCompoundAreaCosts,
} from "./additionalItemsCalculations";
import { generateNotesSheetData } from './notesSheetGenerator';

// Excel Export Interfaces
export interface ExcelWorksheet {
  name: string;
  data: any[][];
}

export interface ExcelWorkbook {
  worksheets: ExcelWorksheet[];
  filename: string;
}

// Matrix Excel Export
export const generateMatrixExcelData = (
  matrixData: MatrixData
): ExcelWorkbook => {
  const worksheets: ExcelWorksheet[] = [];

  // Main Matrix worksheet
  const matrixWorksheet = generateMatrixWorksheet(matrixData);
  worksheets.push(matrixWorksheet);

  // Cash Flow worksheet (if available)
  if (matrixData.cashFlow) {
    const cashFlowWorksheet = generateCashFlowWorksheet(matrixData);
    worksheets.push(cashFlowWorksheet);
  }

  // Summary worksheet
  const summaryWorksheet = generateSummaryWorksheet(matrixData);
  worksheets.push(summaryWorksheet);

  return {
    worksheets,
    filename: `Matrix_${new Date().toISOString().split("T")[0]}.xlsx`,
  };
};

const generateMatrixWorksheet = (matrixData: MatrixData): ExcelWorksheet => {
  const data: any[][] = [];

  // Header rows with enhanced branding
  data.push(["M & J EVANS CONSTRUCTION LIMITED"]);
  data.push(["The Groundworks Partner of Choice"]);
  data.push(["GROUNDWORKS MATRIX"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]); // Empty row

  // Column headers
  const headers = ["Plot", "House Type", "Total Value"];
  const activeStages = matrixData.settings.stages.filter(
    (stage) => stage.isActive
  );
  activeStages.forEach((stage) => headers.push(stage.name));
  data.push(headers);

  // Plot data rows
  matrixData.plotData.forEach((plot) => {
    const row = [plot.plotNumber, plot.houseType, plot.totalPlotValue];

    activeStages.forEach((stage) => {
      row.push(plot.stageValues[stage.id] || 0);
    });

    data.push(row);
  });

  // Totals row
  const totalsRow = ["TOTALS", "", matrixData.projectTotals.adjustedTotal];
  activeStages.forEach((stage) => {
    totalsRow.push(matrixData.stageTotals[stage.id] || 0);
  });
  data.push(totalsRow);

  // Value engineering adjustments (if any)
  const adjustments = matrixData.plotData.filter(
    (plot) => plot.adjustments.length > 0
  );
  if (adjustments.length > 0) {
    data.push([]); // Empty row
    data.push(["VALUE ENGINEERING ADJUSTMENTS"]);
    data.push([
      "Plot",
      "Stage",
      "Original Value",
      "Adjusted Value",
      "Reason",
      "Date",
    ]);

    adjustments.forEach((plot) => {
      plot.adjustments.forEach((adj) => {
        data.push([
          plot.plotNumber,
          adj.stageId,
          adj.originalValue,
          adj.adjustedValue,
          adj.reason,
          adj.date.toLocaleDateString(),
        ]);
      });
    });
  }

  return {
    name: "Matrix",
    data,
  };
};

const generateCashFlowWorksheet = (matrixData: MatrixData): ExcelWorksheet => {
  const data: any[][] = [];

  if (!matrixData.cashFlow) {
    return { name: "Cash Flow", data: [["No cash flow data available"]] };
  }

  // Header rows
  data.push(["CASH FLOW PROJECTION"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]); // Empty row

  // Column headers
  data.push(["Month", "Monthly Value", "Cumulative Value", "Progress %"]);

  // Cash flow data
  const months = Object.keys(matrixData.cashFlow.monthlyValues).sort();
  months.forEach((month) => {
    const monthly = matrixData.cashFlow!.monthlyValues[month];
    const cumulative = matrixData.cashFlow!.cumulativeValues[month];
    const progress =
      (cumulative / matrixData.projectTotals.adjustedTotal) * 100;

    data.push([month, monthly, cumulative, Math.round(progress)]);
  });

  return {
    name: "Cash Flow",
    data,
  };
};

const generateSummaryWorksheet = (matrixData: MatrixData): ExcelWorksheet => {
  const data: any[][] = [];

  // Header rows
  data.push(["PROJECT SUMMARY"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]); // Empty row

  // Project totals
  data.push(["PROJECT TOTALS"]);
  data.push(["Total Plots:", matrixData.plotData.length]);
  data.push(["Project Value:", matrixData.projectTotals.totalValue]);
  data.push(["Value Adjustments:", matrixData.projectTotals.totalAdjustments]);
  data.push(["Adjusted Total:", matrixData.projectTotals.adjustedTotal]);
  data.push([]); // Empty row

  // Stage breakdown
  data.push(["STAGE BREAKDOWN"]);
  data.push(["Stage", "Total Value", "Percentage"]);
  const activeStages = matrixData.settings.stages.filter(
    (stage) => stage.isActive
  );
  activeStages.forEach((stage) => {
    const stageValue = matrixData.stageTotals[stage.id] || 0;
    const percentage =
      (stageValue / matrixData.projectTotals.adjustedTotal) * 100;
    data.push([stage.name, stageValue, Math.round(percentage * 100) / 100]);
  });

  return {
    name: "Summary",
    data,
  };
};

// Additional Items Excel Export
export const generateAdditionalItemsExcelData = (
  additionalItems: AdditionalItemsData
): ExcelWorkbook => {
  const worksheets: ExcelWorksheet[] = [];

  // Retaining Walls worksheet
  if (additionalItems.retainingWalls.length > 0) {
    worksheets.push(
      generateRetainingWallsWorksheet(additionalItems.retainingWalls)
    );
  }

  // Attenuation Crates worksheet
  if (additionalItems.attenuationCrates.length > 0) {
    worksheets.push(
      generateAttenuationCratesWorksheet(additionalItems.attenuationCrates)
    );
  }

  // Compound Areas worksheet
  if (additionalItems.compoundAreas.length > 0) {
    worksheets.push(
      generateCompoundAreasWorksheet(additionalItems.compoundAreas)
    );
  }

  // Custom Calculations worksheet
  if (additionalItems.customCalculations.length > 0) {
    worksheets.push(
      generateCustomCalculationsWorksheet(additionalItems.customCalculations)
    );
  }

  // Summary worksheet
  worksheets.push(generateAdditionalItemsSummaryWorksheet(additionalItems));

  return {
    worksheets,
    filename: `Additional_Items_${new Date().toISOString().split("T")[0]}.xlsx`,
  };
};

const generateRetainingWallsWorksheet = (
  walls: RetainingWallData[]
): ExcelWorksheet => {
  const data: any[][] = [];

  // Header
  data.push(["RETAINING WALLS"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]);

  // Column headers
  data.push([
    "Description",
    "Length (m)",
    "Height (m)",
    "Wall Type",
    "Area (m²)",
    "Excavation (m³)",
    "Concrete (m³)",
    "Reinforcement (kg)",
    "Drainage Required",
    "Drainage Length (m)",
    "Estimated Cost",
    "Notes",
  ]);

  // Data rows
  walls.forEach((wall) => {
    data.push([
      wall.description,
      wall.length,
      wall.height,
      wall.wallType.replace("_", " ").toUpperCase(),
      wall.area,
      wall.excavationVolume,
      wall.concreteVolume,
      wall.reinforcement,
      wall.drainage?.required ? "Yes" : "No",
      wall.drainage?.length || 0,
      calculateRetainingWallCosts(wall),
      wall.notes || "",
    ]);
  });

  // Total
  const totalCost = walls.reduce(
    (sum, wall) => sum + calculateRetainingWallCosts(wall),
    0
  );
  data.push([]);
  data.push(["TOTAL", "", "", "", "", "", "", "", "", "", totalCost, ""]);

  return {
    name: "Retaining Walls",
    data,
  };
};

const generateAttenuationCratesWorksheet = (
  crates: AttenuationCrateData[]
): ExcelWorksheet => {
  const data: any[][] = [];

  // Header
  data.push(["ATTENUATION CRATES"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]);

  // Column headers
  data.push([
    "Description",
    "Storage Volume (m³)",
    "Crate Type",
    "Depth (m)",
    "Cover Depth (m)",
    "Crate Units",
    "Excavation (m³)",
    "Geotextile (m²)",
    "Stone Surround (m³)",
    "Estimated Cost",
    "Notes",
  ]);

  // Data rows
  crates.forEach((crate) => {
    data.push([
      crate.description,
      crate.storageVolume,
      crate.crateType.toUpperCase(),
      crate.depth,
      crate.coverDepth,
      crate.crateUnits,
      crate.excavationVolume,
      crate.geotextile,
      crate.stoneSurround,
      calculateAttenuationCrateCosts(crate),
      crate.notes || "",
    ]);
  });

  // Total
  const totalCost = crates.reduce(
    (sum, crate) => sum + calculateAttenuationCrateCosts(crate),
    0
  );
  data.push([]);
  data.push(["TOTAL", "", "", "", "", "", "", "", "", totalCost, ""]);

  return {
    name: "Attenuation Crates",
    data,
  };
};

const generateCompoundAreasWorksheet = (
  compounds: CompoundAreaData[]
): ExcelWorksheet => {
  const data: any[][] = [];

  // Header
  data.push(["COMPOUND AREAS"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]);

  // Column headers
  data.push([
    "Description",
    "Area (m²)",
    "Duration (weeks)",
    "Surface Type",
    "Fencing Required",
    "Fencing Length (m)",
    "Fencing Type",
    "Power",
    "Water",
    "Telecom",
    "Site Offices",
    "Storage Containers",
    "Welfare Units",
    "Estimated Cost",
    "Notes",
  ]);

  // Data rows
  compounds.forEach((compound) => {
    data.push([
      compound.description,
      compound.area,
      compound.duration,
      compound.surfaceType.toUpperCase(),
      compound.fencing?.required ? "Yes" : "No",
      compound.fencing?.length || 0,
      compound.fencing?.type?.toUpperCase() || "",
      compound.services?.power ? "Yes" : "No",
      compound.services?.water ? "Yes" : "No",
      compound.services?.telecom ? "Yes" : "No",
      compound.structures?.offices || 0,
      compound.structures?.storage || 0,
      compound.structures?.welfare || 0,
      calculateCompoundAreaCosts(compound),
      compound.notes || "",
    ]);
  });

  // Total
  const totalCost = compounds.reduce(
    (sum, compound) => sum + calculateCompoundAreaCosts(compound),
    0
  );
  data.push([]);
  data.push([
    "TOTAL",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    totalCost,
    "",
  ]);

  return {
    name: "Compound Areas",
    data,
  };
};

const generateCustomCalculationsWorksheet = (
  calculations: CustomCalculationData[]
): ExcelWorksheet => {
  const data: any[][] = [];

  // Header
  data.push(["CUSTOM CALCULATIONS"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]);

  calculations.forEach((calc, index) => {
    if (index > 0) data.push([]); // Separator between calculations

    data.push([calc.title]);
    data.push([calc.description]);
    data.push([]);

    // Item headers
    data.push(["Description", "Quantity", "Unit", "Rate", "Amount"]);

    // Items
    calc.items.forEach((item) => {
      data.push([
        item.description,
        item.quantity,
        item.unit,
        item.rate,
        item.amount,
      ]);
    });

    // Total
    data.push([]);
    data.push(["TOTAL", "", "", "", calc.total]);

    if (calc.notes) {
      data.push([]);
      data.push(["Notes:", calc.notes]);
    }
  });

  return {
    name: "Custom Calculations",
    data,
  };
};

const generateAdditionalItemsSummaryWorksheet = (
  additionalItems: AdditionalItemsData
): ExcelWorksheet => {
  const data: any[][] = [];

  // Header
  data.push(["ADDITIONAL ITEMS SUMMARY"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]);

  // Summary by category
  data.push(["Category", "Count", "Total Cost"]);
  data.push([
    "Retaining Walls",
    additionalItems.retainingWalls.length,
    additionalItems.totals.retainingWallsTotal,
  ]);
  data.push([
    "Attenuation Crates",
    additionalItems.attenuationCrates.length,
    additionalItems.totals.attenuationCratesTotal,
  ]);
  data.push([
    "Compound Areas",
    additionalItems.compoundAreas.length,
    additionalItems.totals.compoundAreasTotal,
  ]);
  data.push([
    "Custom Calculations",
    additionalItems.customCalculations.length,
    additionalItems.totals.customCalculationsTotal,
  ]);
  data.push([]);
  data.push(["GRAND TOTAL", "", additionalItems.totals.grandTotal]);

  return {
    name: "Summary",
    data,
  };
};

// PDF Export Utilities
export interface PDFSection {
  title: string;
  content: any[];
}

export interface PDFDocument {
  title: string;
  subtitle: string;
  sections: any;
  footer: string;
}

export const generateMatrixPDFData = (matrixData: MatrixData): PDFDocument => {
  const sections: any = [];

  // Matrix table section
  sections.push({
    title: "Payment Matrix",
    content: generateMatrixPDFTable(matrixData),
  });

  // Summary section
  sections.push({
    title: "Project Summary",
    content: generateMatrixPDFSummary(matrixData),
  });

  // Cash flow section (if available)
  if (matrixData.cashFlow) {
    sections.push({
      title: "Cash Flow Projection",
      content: generateCashFlowPDFTable(matrixData),
    });
  }

  return {
    title: "M & J EVANS CONSTRUCTION LIMITED",
    subtitle: "Groundworks Payment Matrix",
    sections,
    footer: `Generated on ${new Date().toLocaleDateString()}`,
  };
};

const generateMatrixPDFTable = (matrixData: MatrixData) => {
  const activeStages = matrixData.settings.stages.filter(
    (stage) => stage.isActive
  );

  return {
    type: "table",
    headers: [
      "Plot",
      "House Type",
      "Total Value",
      ...activeStages.map((s) => s.name),
    ],
    rows: [
      ...matrixData.plotData.map((plot) => [
        plot.plotNumber,
        plot.houseType,
        `£${plot.totalPlotValue.toLocaleString()}`,
        ...activeStages.map(
          (stage) => `£${(plot.stageValues[stage.id] || 0).toLocaleString()}`
        ),
      ]),
      [
        "TOTALS",
        "",
        `£${matrixData.projectTotals.adjustedTotal.toLocaleString()}`,
        ...activeStages.map(
          (stage) =>
            `£${(matrixData.stageTotals[stage.id] || 0).toLocaleString()}`
        ),
      ],
    ],
  };
};

const generateMatrixPDFSummary = (matrixData: MatrixData) => {
  return {
    type: "summary",
    items: [
      { label: "Total Plots", value: matrixData.plotData.length },
      {
        label: "Project Value",
        value: `£${matrixData.projectTotals.totalValue.toLocaleString()}`,
      },
      {
        label: "Value Adjustments",
        value: `£${matrixData.projectTotals.totalAdjustments.toLocaleString()}`,
      },
      {
        label: "Adjusted Total",
        value: `£${matrixData.projectTotals.adjustedTotal.toLocaleString()}`,
      },
    ],
  };
};

const generateCashFlowPDFTable = (matrixData: MatrixData) => {
  if (!matrixData.cashFlow) return null;

  const months = Object.keys(matrixData.cashFlow.monthlyValues).sort();

  return {
    type: "table",
    headers: ["Month", "Monthly Value", "Cumulative Value", "Progress"],
    rows: months.map((month) => [
      month,
      `£${matrixData.cashFlow!.monthlyValues[month].toLocaleString()}`,
      `£${matrixData.cashFlow!.cumulativeValues[month].toLocaleString()}`,
      `${Math.round(
        (matrixData.cashFlow!.cumulativeValues[month] /
          matrixData.projectTotals.adjustedTotal) *
          100
      )}%`,
    ]),
  };
};

// Export format utilities
export const downloadAsExcel = (workbook: ExcelWorkbook) => {
  // Import xlsx library dynamically
  import("xlsx")
    .then((XLSX) => {
      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Add each worksheet to the workbook
      workbook.worksheets.forEach((worksheet) => {
        // Convert 2D array data to worksheet
        const ws = XLSX.utils.aoa_to_sheet(worksheet.data);

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, worksheet.name);
      });

      // Write file
      XLSX.writeFile(wb, workbook.filename);
    })
    .catch((error) => {
      console.error("Failed to load xlsx library:", error);

      // Fallback to JSON export if xlsx fails
      console.log("Falling back to JSON export");
      const dataStr = JSON.stringify(workbook, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = workbook.filename.replace(".xlsx", ".json");
      link.click();
      URL.revokeObjectURL(url);
    });
};

export const downloadAsPDF = (document: any) => {
  // This would integrate with a library like jsPDF or pdfmake
  console.log("PDF download:", document);

  // Mock download for now
  const dataStr = JSON.stringify(document, null, 2);
  const dataBlob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${document.subtitle.replace(/\s+/g, "_")}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

// Utility to format currency for exports
export const formatCurrency = (value: number): string => {
  return `£${value.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Utility to format percentage for exports
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

// Bill of Quantities Excel Export
export const generateBOQExcelData = (boqData: any): ExcelWorkbook => {
  const worksheets: ExcelWorksheet[] = [];

  // Main BOQ worksheet
  const boqWorksheet = generateBOQWorksheet(boqData);
  worksheets.push(boqWorksheet);

  // Summary worksheet
  const summaryWorksheet = generateBOQSummaryWorksheet(boqData);
  worksheets.push(summaryWorksheet);

  // Sum Analysis worksheet
  const sumAnalysisWorksheet = generateSumAnalysisWorksheet(boqData);
  worksheets.push(sumAnalysisWorksheet);

  // Notes worksheet
  const notesWorksheet = generateNotesWorksheet(boqData);
  worksheets.push(notesWorksheet);

  // Category breakdown worksheets
  if (boqData.items && boqData.items.length > 0) {
    const categories = [
      ...new Set(boqData.items.map((item: any) => item.category)),
    ];
    categories.forEach((category) => {
      if (category) {
        const categoryWorksheet = generateBOQCategoryWorksheet(
          boqData,
          category
        );
        worksheets.push(categoryWorksheet);
      }
    });
  }

  return {
    worksheets,
    filename: `BOQ_${new Date().toISOString().split("T")[0]}.xlsx`,
  };
};

const generateBOQWorksheet = (boqData: any): ExcelWorksheet => {
  const data: any[][] = [];

  // Header rows with enhanced branding
  data.push(["M & J EVANS CONSTRUCTION LIMITED"]);
  data.push(["The Groundworks Partner of Choice"]);
  data.push(["BILL OF QUANTITIES"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  if (boqData.project) {
    data.push(["Project:", boqData.project.name || ""]);
    data.push(["Client:", boqData.project.client || ""]);
    data.push(["Region:", boqData.project.region || ""]);
  }
  data.push([]); // Empty row

  // Column headers
  data.push([
    "Category",
    "Description",
    "Quantity",
    "Unit",
    "Rate (£)",
    "Amount (£)",
  ]);

  // BOQ items
  if (boqData.items && boqData.items.length > 0) {
    boqData.items.forEach((section: any) => {
      // Section header
      data.push([`=== ${section.category} ===`, "", "", "", "", ""]);

      // Section items
      if (section.items && section.items.length > 0) {
        section.items.forEach((item: any) => {
          data.push([
            item.category || "",
            item.description || "",
            typeof item.quantity === "number"
              ? item.quantity.toFixed(2)
              : item.quantity || 0,
            item.unit || "",
            typeof item.rate === "number"
              ? item.rate.toFixed(2)
              : item.rate || 0,
            typeof item.amount === "number"
              ? item.amount.toFixed(2)
              : item.amount || 0,
          ]);
        });
      }

      // Section total
      data.push([
        "",
        `${section.category} TOTAL`,
        "",
        "",
        "",
        typeof section.total === "number"
          ? section.total.toFixed(2)
          : section.total || 0,
      ]);
      data.push([]); // Empty row after each section
    });
  }

  // Grand total
  data.push([
    "",
    "GRAND TOTAL",
    "",
    "",
    "",
    typeof boqData.grandTotal === "number"
      ? boqData.grandTotal.toFixed(2)
      : boqData.grandTotal || 0,
  ]);

  return {
    name: "Bill of Quantities",
    data,
  };
};

const generateBOQSummaryWorksheet = (boqData: any): ExcelWorksheet => {
  const data: any[][] = [];

  // Header rows
  data.push(["BOQ SUMMARY"]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]); // Empty row

  // Project information
  if (boqData.project) {
    data.push(["PROJECT INFORMATION"]);
    data.push(["Project Name:", boqData.project.name || ""]);
    data.push(["Client:", boqData.project.client || ""]);
    data.push(["Region:", boqData.project.region || ""]);
    data.push([]); // Empty row
  }

  // Category breakdown
  data.push(["CATEGORY BREAKDOWN"]);
  data.push(["Category", "Total Cost (£)", "Percentage (%)"]);

  if (boqData.items && boqData.items.length > 0) {
    const grandTotal = boqData.grandTotal || 0;

    boqData.items.forEach((section: any) => {
      const sectionTotal = section.total || 0;
      const percentage = grandTotal > 0 ? (sectionTotal / grandTotal) * 100 : 0;

      data.push([
        section.category || "",
        typeof sectionTotal === "number"
          ? sectionTotal.toFixed(2)
          : sectionTotal,
        typeof percentage === "number" ? percentage.toFixed(2) : percentage,
      ]);
    });
  }

  data.push([]); // Empty row
  data.push([
    "GRAND TOTAL",
    typeof boqData.grandTotal === "number"
      ? boqData.grandTotal.toFixed(2)
      : boqData.grandTotal || 0,
    "100.00",
  ]);

  return {
    name: "Summary",
    data,
  };
};

const generateBOQCategoryWorksheet = (
  boqData: any,
  category: string
): ExcelWorksheet => {
  const data: any[][] = [];

  // Header rows
  data.push([`${category.toUpperCase()} - DETAILED BREAKDOWN`]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]); // Empty row

  // Find the category section
  const categorySection = boqData.items?.find(
    (section: any) => section.category === category
  );

  if (categorySection) {
    // Column headers
    data.push([
      "Item",
      "Description",
      "Quantity",
      "Unit",
      "Rate (£)",
      "Amount (£)",
    ]);

    // Items
    if (categorySection.items && categorySection.items.length > 0) {
      categorySection.items.forEach((item: any, index: number) => {
        data.push([
          index + 1,
          item.description || "",
          typeof item.quantity === "number"
            ? item.quantity.toFixed(2)
            : item.quantity || 0,
          item.unit || "",
          typeof item.rate === "number" ? item.rate.toFixed(2) : item.rate || 0,
          typeof item.amount === "number"
            ? item.amount.toFixed(2)
            : item.amount || 0,
        ]);
      });
    }

    // Category total
    data.push([]);
    data.push([
      "",
      `${category.toUpperCase()} TOTAL`,
      "",
      "",
      "",
      typeof categorySection.total === "number"
        ? categorySection.total.toFixed(2)
        : categorySection.total || 0,
    ]);
  } else {
    data.push(["No data available for this category"]);
  }

  return {
    name: category.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30), // Excel worksheet name restrictions
    data,
  };
};

// Export template generators
export const generateBlankMatrixTemplate = (): ExcelWorkbook => {
  return {
    worksheets: [
      {
        name: "Matrix Template",
        data: [
          ["M&J EVANS CONSTRUCTION LTD"],
          ["GROUNDWORKS MATRIX TEMPLATE"],
          [""],
          [
            "Plot",
            "House Type",
            "Total Value",
            "Site Clearance",
            "Foundation Excavation",
            "Foundation Concrete",
            "Foundation Blockwork",
            "Foundation Floor",
            "Drainage",
            "External Works - Drives",
            "External Works - Gardens",
          ],
          ["001", "Type A", 0, 0, 0, 0, 0, 0, 0, 0, 0],
          ["002", "Type B", 0, 0, 0, 0, 0, 0, 0, 0, 0],
          ["003", "Type A", 0, 0, 0, 0, 0, 0, 0, 0, 0],
        ],
      },
    ],
    filename: "Matrix_Template.xlsx",
  };
};

export const generateAdditionalItemsTemplate = (): ExcelWorkbook => {
  return {
    worksheets: [
      {
        name: "Retaining Walls",
        data: [
          ["RETAINING WALLS TEMPLATE"],
          [""],
          [
            "Description",
            "Length (m)",
            "Height (m)",
            "Wall Type",
            "Drainage Required",
            "Notes",
          ],
          [
            "Example Wall 1",
            10,
            1.5,
            "mass_concrete",
            "Yes",
            "Sample retaining wall",
          ],
          [
            "Example Wall 2",
            15,
            2.0,
            "reinforced_concrete",
            "No",
            "Higher wall example",
          ],
        ],
      },
      {
        name: "Attenuation Crates",
        data: [
          ["ATTENUATION CRATES TEMPLATE"],
          [""],
          [
            "Description",
            "Storage Volume (m³)",
            "Crate Type",
            "Depth (m)",
            "Cover Depth (m)",
            "Notes",
          ],
          [
            "Example System 1",
            50,
            "polypipe",
            2.0,
            0.5,
            "Sample attenuation system",
          ],
          ["Example System 2", 100, "graf", 2.5, 0.6, "Larger system example"],
        ],
      },
    ],
    filename: "Additional_Items_Template.xlsx",
  };
};

// Sum Analysis worksheet generator
const generateSumAnalysisWorksheet = (boqData: any): ExcelWorksheet => {
  const data: any[][] = [];

  // Header rows with enhanced branding
  data.push(["M&J EVANS CONSTRUCTION LIMITED"]);
  data.push(["The Groundworks Partner of Choice"]);
  data.push(["BAYSTON HILL"]);
  data.push(["GENERAL SUMMARY"]);
  data.push([]);
  data.push(["Generated:", new Date().toLocaleDateString()]);
  data.push([]);

  // Column headers
  data.push(["Description", "Amount (£)"]);

  // Main category header
  data.push(["ROADS & SEWERS", ""]);

  // Get totals from BOQ data
  const preliminariesTotal =
    boqData.items.find((item: any) => item.category === "PRELIMINARY ITEMS")
      ?.total || 0;
  const foulSewersTotal =
    boqData.items.find((item: any) => item.category === "FOUL SEWERS")?.total ||
    0;
  const stormSewersTotal =
    boqData.items.find((item: any) => item.category === "STORM SEWERS")
      ?.total || 0;
  const roadsTotal =
    boqData.items.find((item: any) => item.category === "ROADS")?.total || 0;
  const mainServicesTotal =
    boqData.items.find((item: any) => item.category === "MAIN SERVICES")
      ?.total || 0;

  // Data rows
  data.push(["PRELIMINARIES", preliminariesTotal.toFixed(2)]);
  data.push(["FOUL SEWERS", foulSewersTotal.toFixed(2)]);
  data.push([
    "SAVING SHOULD FOUL DEMARCATION CHAMBERS BE PPIC CHAMBERS IN LIEU OF PCC",
    "-22396.00",
  ]);
  data.push(["PUMP STATION & RISING MAIN", mainServicesTotal.toFixed(2)]);
  data.push(["STORM SEWERS", stormSewersTotal.toFixed(2)]);
  data.push([
    "SAVING SHOULD STORM DEMARCATION CHAMBERS BE PPIC CHAMBERS IN LIEU OF PCC",
    "-15873.00",
  ]);
  data.push([
    "SAVING FOR AS-DUG BACKFILL TO SEWER TRENCHES IN SOFT LANDSCAPED LOCATIONS",
    "-7175.68",
  ]);
  data.push([
    "SAVING FOR USING RECYCLED 6F2 AS SEWER BACKFILL IN LIEU OF TYPE 1",
    "-106170.35",
  ]);
  data.push(["S38 WORKS TO ACHIEVE 5%+ CBR", roadsTotal.toFixed(2)]);

  // EO items
  data.push([
    "EO FOR 350mm CAPPING LAYER IN 6F2 (CBR 4%): STOCKPILE SURPLUS",
    "42009.00 (If Required)",
  ]);
  data.push([
    "EO REMOVE SURPLUS ARISINGS FROM 350mm CAPPING",
    "21974.00 (If Required)",
  ]);
  data.push([
    "EO FOR 450mm CAPPING LAYER IN 6F2 (CBR 3%): STOCKPILE SURPLUS",
    "64585.00 (If Required)",
  ]);
  data.push([
    "EO REMOVE SURPLUS ARISINGS FROM 450mm CAPPING",
    "34095.00 (If Required)",
  ]);
  data.push([
    "EO FOR 600mm CAPPING LAYER IN 6F2 (CBR 2%): STOCKPILE SURPLUS",
    "102405.00 (If Required)",
  ]);
  data.push([
    "EO REMOVE SURPLUS ARISINGS FROM 600mm CAPPING",
    "54384.00 (If Required)",
  ]);
  data.push(["MAINS SERVICE TRENCHING", mainServicesTotal.toFixed(2)]);

  data.push([]);

  // Calculate total
  const totalRoadsAndSewers =
    preliminariesTotal +
    foulSewersTotal +
    stormSewersTotal +
    roadsTotal +
    mainServicesTotal -
    22396 -
    15873 -
    7175.68 -
    106170.35;
  data.push(["TOTAL ROADS & SEWERS", totalRoadsAndSewers.toFixed(2)]);

  return {
    name: "Sum Analysis",
    data,
  };
};

// Notes worksheet generator
const generateNotesWorksheet = (boqData: any): ExcelWorksheet => {
  const data = generateNotesSheetData({
    projectName: boqData.project?.name,
    clientName: boqData.project?.client,
  });

  return {
    name: "Notes",
    data,
  };
};
